import { CloseOutlined } from '@ant-design/icons';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { FC } from 'react';

import BestValueBadge from '@/components/PlatformSubscription/BestValueBadge';
import {
  borderColorClassName,
  borderT,
} from '@/components/PlatformSubscription/PlanTable';
import Pricing from '@/components/PlatformSubscription/PlanTable/Columns/HeaderCell/Pricing';
import { plansInterface } from '@/components/PlatformSubscription/PlanTable/ResponsiveTable';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';

interface Props {
  index: number;
  item: plansInterface;
  setData?: React.Dispatch<React.SetStateAction<number[]>>;
  data: number[];
  items: plansInterface[];
}

const Item: FC<Props> = ({ index, item, setData, data }) => {
  const router = useRouter();
  const isAffiliate = router?.pathname?.includes('affiliate');
  const titleClass = ' text-sm font-normal leading-[22px]';

  const isOpen = data.includes(item.id);

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: item.id * 0.1 + 0.2 }}
    >
      <motion.button
        onClick={() =>
          setData?.((prev) =>
            prev.includes(item.id)
              ? prev.filter((id) => id !== item.id)
              : [...prev, item.id],
          )
        }
        className={`
          ${borderColorClassName}
          ${borderT}
          ${isOpen ? 'bg-primary/15' : 'bg-black'}
          px-6 py-5 flex items-center justify-between w-full
        `}
      >
        <p
          className={`
            ${isOpen ? 'text-primary' : 'text-white'}
            text-sm leading-[22px] font-semibold
          `}
        >
          {isAffiliate ? item.billed : `Billed ${item.billed}`}
        </p>
        <motion.div
          animate={{ rotate: isOpen ? -180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className='h-6 w-6 text-white' />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='overflow-hidden'
          >
            <div className='flex flex-col items-center px-6 border-b border-white/20'>
              {item.bestValue && (
                <div className='pt-6'>
                  <BestValueBadge
                    classes={{
                      parantClass:
                        'bg-gradient-to-b from-[#CD4FF2] to-[#5F4AFF]',
                      textParant: 'bg-[#170F33]',
                    }}
                  />
                </div>
              )}
              <Pricing
                expiresIn={item.expiresIn}
                className='pt-6'
                price={item.monthlyPrice}
                priceCut={isAffiliate ? '4999' : '/Month'}
              />
              <Tooltip title={item.buttonTooltip}>
                <ButtonDefault
                  disabled={item.disabled}
                  onClick={item.onClick}
                  className='w-full px-6 py-[9px] text-sm font-semibold leading-[22px] mb-6 disabled:bg-grayZl-900 disabled:text-grayZl-700'
                  size={0}
                  variant={ButtonVariants.PRIMARY}
                >
                  {item.buttonText}
                </ButtonDefault>
              </Tooltip>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center '>
              <p className={`${titleClass} flex-1`}>eBooks</p>
              <p className={`${titleClass} flex-1 text-right`}>{item.eBook}</p>
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Video Summary</p>
              {item.videoSummary && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>
            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>AudioBooks</p>
              {item.audioBook && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>
            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Certification</p>
              {item.certification && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>

            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>App Access</p>
              {item.appAccess && (
                <CheckIcon className='w-[22px] h-[22px] text-success-400' />
              )}
            </div>
            <div className='flex gap-2.5 py-[17px] px-6 justify-between items-center'>
              <p className={`${titleClass} flex-1`}>Upgrade to Hardcopy</p>
              {item.upgradetoHardcopy ? (
                '25% OFF'
              ) : (
                <CloseOutlined className='w-[22px] h-[22px] text-error-600' />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Item;
